import type { CreateUniLoginHostedAuthRequestResponse, UniLoginProvider } from '@unipile/types';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../../../libs/axios';
import type { QueryConfig } from '../../../types/react-query-utility';

export async function createUniLoginRequest({
  provider,
}: {
  provider: UniLoginProvider;
}): Promise<CreateUniLoginHostedAuthRequestResponse> {
  const response = await axiosInstance.post<CreateUniLoginHostedAuthRequestResponse>('/v2/auth/unilogin/request', {
    provider,
  });

  return response.data;
}

/**
 * A query rather than a mutation: the Hosted Auth session has a single UniLogin request per provider, so it is
 * created once and reused when the form remounts (Strict Mode, switching between login method tabs).
 */
export const createUniLoginRequestQueryOptions = (provider: UniLoginProvider) => {
  return queryOptions({
    queryKey: ['unilogin-request', provider],
    queryFn: () => createUniLoginRequest({ provider }),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

type UseUniLoginRequestOptions = {
  provider: UniLoginProvider;
  queryConfig?: QueryConfig<typeof createUniLoginRequestQueryOptions>;
};

export function useUniLoginRequest({ provider, queryConfig }: UseUniLoginRequestOptions) {
  return useQuery({
    ...createUniLoginRequestQueryOptions(provider),
    ...queryConfig,
  });
}
